import { LoadingButton } from "@mui/lab";
import { Dialog, Stack, DialogContent, TextField, DialogActions, Button, InputAdornment, IconButton, Tooltip } from "@mui/material";
import { DialogTitleClosable } from "../../../components/DialogTitleClosable";
import { bhpButtonSx } from "../../../styles/bhpStyles";
import { CardSwapSearchResultDto } from "../dtos/CardSwapSearchResultDto";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import UpdateIcon from '@mui/icons-material/Update';
import CancelIcon from '@mui/icons-material/Cancel';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useContext, useState } from "react";
import { CardSwapUpdateDto } from "../dtos/CardSwapUpdateDto";
import VspPortalContext from "../../../store/VspPortalContext";
import { useCardSwapHttp } from "../hooks/useCardSwapHttp";
import EditIcon from '@mui/icons-material/Edit';
import { CardSwapStatusCode, cardSwapStatusCodes } from "../enums/CardSwapStatusCode";
import { useCardSwap } from "../hooks/useCardSwap";
import { DialogResult } from "../enums/DialogResult";



type NewCardCreatedDialogProps = {

    cardSwapSearchResultDto : CardSwapSearchResultDto;
    dialogOpen: boolean;
    onClose: ( dialogResult : DialogResult ) => void;

};


export const NewCardCreatedDialog : React.FC< NewCardCreatedDialogProps > = ( props : NewCardCreatedDialogProps ) => {

    const { cardSwapSearchResultDto, dialogOpen, onClose } = props;

    const [ cardNumber, setCardNumber ] = useState< string >( cardSwapSearchResultDto.newCardNumber );
    const [ editingEnabled, setEditingEnabled ] = useState< boolean >( false );
    const [ isCardNumberValid, setIsCardNumberValid ] = useState< boolean >( true );
    const [ isSendingNotification, setIsSendingNotification ] = useState< boolean >( false );
    const [ isUpdated, setIsUpdated ] = useState< boolean >( false );
    const [ isUpdating, setIsUpdating ] = useState< boolean >( false );

    const { state } = useContext( VspPortalContext );

    const { updateCardSwap } = useCardSwapHttp( );

    const { cardSwapSearchResult, setCardSwapSearchResult } = useCardSwap( cardSwapSearchResultDto );


    const checkCardNumberValidity = ( cardNumber : string ) => {

        const containsNumbersOnly = /^\d+$/.test( cardNumber );

        if( containsNumbersOnly && cardNumber.length >= 6 && cardNumber.length <= 8 ){

            setIsCardNumberValid( true );

        } else {

            setIsCardNumberValid( false );

        }

    };

    
    const handleClose = ( ) => {

        if( !isSendingNotification && !isUpdating ) {
            
            
            if( isUpdated ) {
                
                onClose( DialogResult.Update );

            } else {
                
                onClose( DialogResult.Nothing );
                
            }

        }

    }

    
    const handleUpdateClick = ( ) => update( );


    const handleCardNumberChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        const value = event.target.value;
        setCardNumber( value );
        checkCardNumberValidity( value );

    };

    
    const handleCardNumberKeyUp = async ( event: React.KeyboardEvent<HTMLDivElement> ) => {

        if( event.key !== 'Enter' || !isCardNumberValid ) {
            
            return;

        }

        update( );

    };


    const handleEditButtonClick = () => setEditingEnabled( true );


    const handleNotifyForCollectionClick = ( ) => notify( );


    const notify = async( ) => { 

        const cardSwapUpdateDto : CardSwapUpdateDto = {

            cardSwapStatusCode : CardSwapStatusCode.ReadyToCollect,
            id : cardSwapSearchResult.id,
            updatedBy : state.username

        };

        setIsSendingNotification( true );
        const isSuccess = await updateCardSwap( cardSwapUpdateDto );
        setIsSendingNotification( false );


        if( isSuccess ){
            
            onClose( DialogResult.UpdateWithRedirect );

        }

    };

    
    const update = async( ) => { 

        const cardSwapUpdateDto : CardSwapUpdateDto = {

            id : cardSwapSearchResult.id,
            newCardNumber : cardNumber,
            updatedBy : state.username

        };

        setIsUpdating( true );
        const isSuccess = await updateCardSwap( cardSwapUpdateDto );
        setIsUpdating( false );


        if( isSuccess ){
            
            setEditingEnabled( false );
            setIsUpdated( true );
            onClose( DialogResult.Update );

        }

    };

    return(
        
        <Dialog
            onClose={ handleClose }
            open={ dialogOpen }
            sx={{ '& .MuiDialog-paper': { maxHeight: 435, width: '500px' } }}
        >
            <DialogTitleClosable onClose={ handleClose }>
                <Stack direction="row" display="flex" alignItems="center" gap={ 0.5 }>
                    <CreditCardIcon />
                    { cardSwapStatusCodes[ cardSwapSearchResultDto.refCardSwapStatusCode ] }
                </Stack>
            </DialogTitleClosable>

            <DialogContent dividers>
                
                <ul>

                    <li><b>First Name:</b> { cardSwapSearchResult.firstName }</li>
                    <li><b>Last Name:</b> { cardSwapSearchResult.surname }</li>
                    <li><b>Email:</b> { cardSwapSearchResult.email }</li>
                    <li><b>Employee / ERMS ID:</b> { cardSwapSearchResult.employeeNumber }</li>
                    <li><b>Card Collection Village:</b> { cardSwapSearchResult.villageCode }</li>

                </ul>

                <TextField 
                    sx={{ width : '100%' }} 
                    label="New Card Number" 
                    variant="outlined"
                    value={ cardNumber }
                    onChange={ handleCardNumberChange }
                    onKeyUp={ handleCardNumberKeyUp }
                    disabled={ isSendingNotification || isUpdating || !editingEnabled }
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title="Enable editing">
                                    <IconButton
                                        onClick={ ( ) => handleEditButtonClick( ) }
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        )
                    }}
                />
                
                { 
                    ( !isCardNumberValid && cardNumber )
                        ?
                    <span style={{ color : 'red', fontSize : '12px' }}>
                        * Card number should contain numbers only from 6 to 8 digits
                    </span>
                        :
                    <span style={{ fontSize : '12px' }}>
                        &nbsp;
                    </span>

                }


            </DialogContent>

            <DialogActions>
                {

                    ( cardNumber !== cardSwapSearchResult.newCardNumber )
                        ?
                    <LoadingButton
                        disabled={ !isCardNumberValid || isUpdating }
                        loading={ isUpdating }
                        loadingPosition="start"
                        onClick={ handleUpdateClick } 
                        startIcon={ <UpdateIcon /> }
                        sx={{ ...bhpButtonSx, width: '100px' }} 
                        variant="contained"
                    >
                        Update
                    </LoadingButton>
                        :
                    <LoadingButton
                        disabled={ isSendingNotification }
                        loading={ isSendingNotification }
                        loadingPosition="start"
                        onClick={ handleNotifyForCollectionClick } 
                        startIcon={ <NotificationsActiveIcon /> }
                        sx={{ ...bhpButtonSx }} 
                        variant="contained"
                    >
                        Notify For Collection
                    </LoadingButton>

                }

                <Button 
                    disabled={ isUpdating || isSendingNotification }
                    onClick={ handleClose } 
                    startIcon={ <CancelIcon /> }
                    sx={{ ...bhpButtonSx, width: '100px' }} 
                >
                    Close
                </Button>

            </DialogActions>

            

        </Dialog>

    );

}
